import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api, { unwrap } from '../utils/api';
import CategoryFilterSidebar from '../components/CategoryFilterSidebar';
import isMobileLayout from '../utils/isMobileLayout';
import useSeo from '../hooks/useSeo';
import Icon from '../components/Icon';

export default function Category() {
  const { slug, subSlug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileLayout());

  // Use subSlug if it exists (subcategory), otherwise use slug (main category)
  const activeSlug = subSlug || slug;

  const selected = useMemo(() => {
    const s = sp.get('sub');
    return s ? s.split(',').filter(Boolean) : [];
  }, [sp]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    Promise.all([
      api.get(`/products/GetProductsByCategoryFriendlyName/${activeSlug}`).then(r => unwrap(r) || []).catch(() => []),
      api.get(`/categories/GetCategoryByFriendlyName/${activeSlug}`).then(r => unwrap(r)).catch(() => null),
    ])
      .then(([prods, cat]) => {
        setProducts(prods);
        setCategory(cat);
      })
      .finally(() => setLoading(false));
  }, [activeSlug]);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function toggleFilter(opt) {
    const next = selected.includes(opt)
      ? selected.filter(s => s !== opt)
      : [...selected, opt];
    const params = new URLSearchParams(sp);
    if (next.length) params.set('sub', next.join(','));
    else params.delete('sub');
    setSp(params);
  }

  function clearFilters() {
    const params = new URLSearchParams(sp);
    params.delete('sub');
    setSp(params);
  }

  const filtered = selected.length
    ? products.filter(p =>
        (p.tags || []).some(t =>
          selected.some(s => t.toLowerCase().includes(s.toLowerCase()))
        )
      )
    : products;

  const hasFilters = !!(category?.filterOptions?.length);
  const title = category?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  useSeo({
    title: `${title} Invitations`,
    description: `Browse custom ${title.toLowerCase()} invitation templates. Personalize Hebrew & English designs online and download print-ready PNG or PDF in minutes.`,
    path: subSlug ? `/categories/${slug}/${subSlug}` : `/categories/${slug}`,
  });

  return (
    <>
      {hasFilters && isMobile && filterModalOpen && (
        <div className="filter-modal-overlay" onClick={() => setFilterModalOpen(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Filters</h3>
              <button className="close-btn" onClick={() => setFilterModalOpen(false)} aria-label="Close filters"><Icon name="close" size="28px" /></button>
            </div>
            <CategoryFilterSidebar
              category={category}
              selected={selected}
              onToggle={toggleFilter}
              onClear={clearFilters}
              isMobileModal
            />
          </div>
        </div>
      )}
      <div className={`cat-page-layout ${hasFilters && !isMobile ? '' : 'no-sidebar'}`}>
        {hasFilters && !isMobile && (
          <CategoryFilterSidebar
            category={category}
            selected={selected}
            onToggle={toggleFilter}
            onClear={clearFilters}
          />
        )}
        <div>
          <div className="cat-page-header">
            <div className="cat-page-header-top">
              <h1>{title}</h1>
              {hasFilters && isMobile && (
                <button className="filters-btn" onClick={() => setFilterModalOpen(true)}>
                  Filters <Icon name="keyboard_arrow_down" />
                </button>
              )}
            </div>
            <p className="count">{filtered.length} {filtered.length === 1 ? 'design' : 'designs'}{selected.length ? ` · ${selected.length} filter${selected.length > 1 ? 's' : ''}` : ''}</p>
          </div>
        {loading ? (
          <p style={{ textAlign: 'center', padding: 40 }}>Loading templates…</p>
        ) : (
          <div className="product-grid">
            {filtered.map(p => (
              <Link key={p._id} to={`/card/${p._id}`} className="product-card">
                <img
                  src={p.thumbnailWithPlaceholders || p.filesId?.thumbnail || p.filesId?.pathWithWatermark}
                  alt={p.name}
                  loading="lazy"
                />
                <div className="meta">
                  <span className="name">{p.name}</span>
                  <span className="price">${Number(p.price || 29.99).toFixed(2)}</span>
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <p style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', padding: 40 }}>
                No templates match these filters yet.
              </p>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
}
